import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreateTransactionRequestDto,
  TransactionRequestsFacade,
} from './transaction-requests.facade';
import {
  MetalRef,
  TransactionRequestV4,
} from '@models/resources/transaction-request.model';
import { DEMO_TRANSACTIONS_V4 } from '@data/fixtures/transactions-v4.fixtures';
import { DEMO_DEPOT_OVERVIEW } from '@data/fixtures/depot.fixtures';
import {
  dummyError,
  dummyResponse,
  shouldSimulateError,
} from '@data/dummy-response.util';

/**
 * In-Memory-Facade (V4). Beim ersten Zugriff wird eine mutable Kopie der
 * Fixtures erstellt — Subsequent-Calls sehen die Änderungen, sodass der
 * Demo-Flow nach Submit den neuen Eintrag oben in der Liste zeigt.
 *
 * `lookupReferencePrice` ruft den V4-Preis (nullable) aus der V4-Depot-
 * Fixture ab. Pt/Pd sind dort `is_active=false` mit Preis `null` — die
 * Customer-Form bietet sie momentan noch zur Auswahl an (Aurum-Klärung
 * U-15 offen), aber beim Submit fällt der Preis auf 0.
 */
@Injectable()
export class DummyTransactionRequestsFacade implements TransactionRequestsFacade {
  private readonly store: TransactionRequestV4[] = [...DEMO_TRANSACTIONS_V4];
  private nextId = Math.max(...DEMO_TRANSACTIONS_V4.map((t) => t.id)) + 1;

  list(): Observable<TransactionRequestV4[]> {
    return dummyResponse('customer.transactions', [...this.store]);
  }

  create(dto: CreateTransactionRequestDto): Observable<TransactionRequestV4> {
    if (shouldSimulateError('customer.transactions')) {
      return dummyError('customer.transactions');
    }
    const metal = this.resolveMetal(dto.metal_id);
    const referencePrice = this.lookupReferencePrice(dto.metal_id);
    const now = new Date().toISOString();
    const created: TransactionRequestV4 = {
      id: this.nextId++,
      metal,
      type: dto.type,
      status: 'open',
      amount_eur: dto.type === 'buy' ? (dto.amount_eur ?? null) : null,
      requested_grams: dto.type === 'sell' ? (dto.requested_grams ?? null) : null,
      note: dto.note?.trim() ? dto.note.trim() : null,
      reference_price: referencePrice,
      invoice_total_eur: null,
      invoice_note: null,
      purchase_date: null,
      purchase_fix_eur_per_gram: null,
      purchased_grams: null,
      invoice: null,
      created_at: now,
      updated_at: now,
    };
    this.store.unshift(created);
    return dummyResponse('customer.transactions', created);
  }

  private resolveMetal(metalId: number): MetalRef {
    const pos = DEMO_DEPOT_OVERVIEW.positions.find((p) => p.metal_id === metalId);
    if (!pos) {
      // Fallback: Gold (sollte in der Praxis nicht passieren, weil das
      // Formular die Auswahl auf existierende Metalle einschränkt).
      return { id: 1, symbol: 'Au', name: 'Gold' };
    }
    return {
      id: pos.metal_id,
      symbol: pos.metal_symbol,
      name: pos.metal_name,
    };
  }

  private lookupReferencePrice(metalId: number): number | null {
    const pos = DEMO_DEPOT_OVERVIEW.positions.find((p) => p.metal_id === metalId);
    return pos?.price_per_gram_net_eur ?? null;
  }
}
